import { useState } from "react";
import Swal from "sweetalert2";
import {
  useMonKycQuery,
  useSoumettreKycMutation,
} from "../../../../utils/api/kyc/kyc.api";
import { KYC_STATUT, TYPES_PIECE } from "../../../../utils/api/kyc/kyc.type";
import { COLORS } from "../../../../utils/constants";
import CapturePhoto from "../../../shared/CapturePhoto";
import FrontFooter from "../../../shared/FrontFooter/FrontFooter";
import FrontHeader from "../../../shared/FrontHeader/FrontHeader";
import Sidebar from "../Sidebar/Sidebar";

// Vérification d'identité, exigée avant tout retrait. Le selfie se prend à la
// caméra : c'est lui qui relie la personne au document.
function VerificationIdentite() {
  const { data: etat, isLoading } = useMonKycQuery();
  const [soumettre, { isLoading: envoi }] = useSoumettreKycMutation();

  const [typePiece, setTypePiece] = useState("cni");
  const [numero, setNumero] = useState("");
  const [recto, setRecto] = useState<File | null>(null);
  const [verso, setVerso] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [erreur, setErreur] = useState("");

  const statut = (etat as any)?.statut || null;
  const motif = (etat as any)?.motif;

  const envoyer = async () => {
    setErreur("");
    if (!numero.trim()) return setErreur("Saisissez le numéro de votre pièce.");
    if (!recto) return setErreur("La photo du recto est requise.");
    if (!selfie)
      return setErreur("La photo de vous avec la pièce en main est requise.");

    const fd = new FormData();
    fd.append("type_piece", typePiece);
    fd.append("numero_piece", numero.trim());
    fd.append("recto", recto);
    if (verso) fd.append("verso", verso);
    fd.append("selfie", selfie);

    const res: any = await soumettre(fd);
    if (res?.error) {
      return setErreur(res.error?.data?.message || "L'envoi a échoué. Réessayez.");
    }
    Swal.fire({
      icon: "success",
      title: "Dossier envoyé",
      text: "Un administrateur l'examinera. Vous serez notifié.",
      iconColor: COLORS.green,
      confirmButtonColor: COLORS.green,
    });
  };

  const formulaireVisible =
    !isLoading && statut !== "valide" && statut !== "en_attente";

  return (
    <div className="campagne-page-container">
      <FrontHeader />
      <div className="mon-compte">
        <div className="row g-0">
          <Sidebar index={5} />
          <div className="col-md-8 col-lg-9 col-xl-10 dashboard-page-content px-3 px-xl-4">
            <div className="mt-tableau-bord pt-4 pb-5">
              <div className="col-md-12 maajjal-table-card px-3 py-4">
                <h3 className="tb-mes-projet-titre mb-2">
                  Vérification d'identité
                </h3>
                <p className="modal-investissement-description mb-4">
                  Elle est exigée avant toute demande de retrait.
                </p>

                {isLoading && <p>Chargement…</p>}

                {statut && (
                  <div
                    className="p-3 mb-4"
                    style={{
                      background: "#fff",
                      borderLeft: `4px solid ${KYC_STATUT[statut].color}`,
                      boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                      Statut
                    </div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: KYC_STATUT[statut].color,
                      }}
                    >
                      {KYC_STATUT[statut].label}
                    </div>
                    {statut === "refuse" && motif && (
                      <div className="mt-2" style={{ fontSize: 13 }}>
                        Motif : {motif}
                      </div>
                    )}
                    {statut === "en_attente" && (
                      <div className="mt-2" style={{ fontSize: 13 }}>
                        Votre dossier est en cours d'examen. Vous pourrez
                        demander un retrait dès qu'il sera accepté.
                      </div>
                    )}
                    {statut === "valide" && (
                      <div className="mt-2" style={{ fontSize: 13 }}>
                        Vous pouvez demander des retraits sur vos projets.
                      </div>
                    )}
                  </div>
                )}

                {formulaireVisible && (
                  <>
                    {statut === "refuse" && (
                      <p className="modal-investissement-description mb-3">
                        Corrigez ce qui a été signalé et renvoyez votre dossier.
                      </p>
                    )}

                    <div className="modal-investissement-montants-input">
                      <label className="modal-investissement-description mb-1 d-block">
                        Type de pièce
                      </label>
                      <select
                        className="form-select"
                        value={typePiece}
                        onChange={(e) => setTypePiece(e.target.value)}
                      >
                        {TYPES_PIECE.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Numéro de la pièce"
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                      />
                    </div>

                    <CapturePhoto
                      label="Recto de la pièce"
                      aide="Photographiez-la ou choisissez un fichier."
                      facingMode="environment"
                      autoriserFichier
                      onCapture={setRecto}
                    />
                    <CapturePhoto
                      label="Verso de la pièce (facultatif)"
                      aide="Inutile pour un passeport."
                      facingMode="environment"
                      autoriserFichier
                      onCapture={setVerso}
                    />
                    <CapturePhoto
                      label="Photo de vous, pièce en main"
                      aide="À prendre maintenant avec votre caméra : le visage et la pièce doivent être lisibles. Le téléversement n'est pas accepté pour cette photo."
                      facingMode="user"
                      onCapture={setSelfie}
                    />

                    {erreur && (
                      <div className="alert alert-danger sia-alert-danger closer mt-2">
                        {erreur}
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={envoi}
                      className="btn modal-investissement-montants-btn-continuer mt-3"
                      onClick={envoyer}
                    >
                      {envoi ? "Envoi…" : "Envoyer mon dossier"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <FrontFooter />
    </div>
  );
}

export default VerificationIdentite;
